import { useEffect, useRef } from 'react';
import { iSigninConfig } from '@models/config';
import '@components/oktaSignInWidget/oktaSignInWidget.css';
import { Box } from '@mui/material';

const presetClassMap: Record<string, string> = {
  'insureon-events': 'preset-insureon-event-okta',
};

interface iSigninWidget {
  config: iSigninConfig;
  onSuccess: (tokens: string) => void;
  onError: (err: Error) => void;
  widget: any;
  preset?: string;
}
export const OktaSignInWidget = ({ config, onSuccess, onError, widget, preset }: iSigninWidget) => {
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const setUpWidget: () => void = () => {
      if (!widgetRef.current) return false;

      widget
        .showSignInToGetTokens({
          el: widgetRef.current,
        })
        .then(onSuccess)
        .catch(onError);

      return () => widget.remove();
    };
    setUpWidget();
    return () => widget.remove();
    // eslint-disable-next-line
  }, [config, onSuccess, onError]);
  return (
    <Box className={preset ? presetClassMap[preset.toLowerCase()] : undefined} sx={oktaStyleOverrides}>
      <div ref={widgetRef} />
    </Box>
  );
};

const commonButtonStyles = {
  // enroll-factor-list
  fontSize: '14px',

  color: 'secondary.main',
  fontWeight: 'bold !important',

  ':hover': {
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
};

const oktaStyleOverrides = {
  '#okta-sign-in': {
    '&.main-container': {
      minHeight: '200px',
    },

    '.auth-content': {
      // LOGIN FORM SHOW FORGOT PASSWORD LINKS
      '.link.help.js-help': {
        display: 'none !important',
      },

      '#help-links-container': {
        display: 'block !important',
      },
    },

    // okta v7 styles
    label: {
      fontSize: '12px !important',
    },

    '.btn-customAuth': {
      ...commonButtonStyles,
    },

    // move footer buttons to seperate lines
    '.auth-footer': {
      a: {
        // put buttom buttons on new lines
        display: 'block',
        mb: 0.2,
      },
    },

    '.resend-link': {
      ...commonButtonStyles,
    },
    '.mfa-verify, .mfa-verify-passcode, .phone-authenticator-challenge, .select-authenticator-authenticate--okta_verify':
      {
        '.identifier': {
          display: 'none',
        },
      },

    '.select-authenticator-enroll, .select-authenticator-enroll--security_question, .select-authenticator-unlock-account, .forgot-password, .identify-recovery, .authenticator-verification-data--okta_email':
      {
        '.resend-link, .link-button': {
          ...commonButtonStyles,
        },
      },

    // forgot password 'send email + use token/resend email screen'
    '.mfa-verify-passcode, .authenticator-verification-data--okta_email': {
      '.okta-form-subtitle': {
        textTransform: 'initial',
        pt: 2,
        pb: 2,
        fontWeight: 'normal',
        fontSize: 14,
      },
    },

    // unlock account screen
    '.select-authenticator-unlock-account': {
      '.okta-form-subtitle': {
        textTransform: 'initial',
        pb: 2,
        fontWeight: 'normal',
        fontSize: 14,
      },

      h3: {
        mt: 1,
        fontSize: 16,
      },
    },

    '.oie-enroll-ov-poll, .password-authenticator,.phone-authenticator-enroll, .select-authenticator-enroll--okta_verify':
      {
        '.identifier-container': {
          display: 'none',
        },
      },

    '.authenticator-enroll-list-container': {
      '.authenticator-list-title': {
        display: 'none',
      },
    },

    // set up okta verify app
    '.oie-enroll-ov-poll': {
      ol: {
        paddingLeft: '12px',
      },
    },

    // reset password screen
    '.password-authenticator, .mfa-verify-password': {
      fontSize: 14,

      ul: {
        marginTop: 0.5,
        paddingLeft: 1,
      },
      li: {
        fontSize: 12,
        fontWeight: 'normal',
      },

      '.password-authenticator--heading': {
        fontWeight: 'bold',
      },
    },
    '.resend-email-view': {
      fontWeight: 'normal',
      fontSize: 14,
    },

    // set up security questions
    '.authenticator-verification-data--okta_email, .enroll-question, .select-authenticator-enroll, .select-authenticator-enroll--security_question':
      {
        '.identifier-container, .authenticator-list-title': {
          display: 'none',
        },

        '.okta-form-subtitle': {
          textTransform: 'initial',
          pt: 2,
          fontWeight: 'normal',
          fontSize: 14,
        },
      },

    // enter password screen
    '.mfa-verify-password': {
      '.auth-footer': {
        a: {
          display: 'block',
          mb: 0.5,
        },
      },
    },

    '.mfa-verify-passcode': {
      '.identifier-container': {
        display: 'none',
      },
      '.okta-form-subtitle': {
        textTransform: 'initial',
        pt: 2,
        pb: 2,
        fontWeight: 'normal',
        fontSize: 14,
      },

      '.button-link': {
        ...commonButtonStyles,
        fontWeight: 600,
        background: 'none',
        border: 'none',
        // textDecoration: 'underline',
        cursor: 'pointer',
        padding: 0,
        margin: 0,
        transition: 'color 0.2s',
      },
    },

    '.o-form-info-container': {
      '.okta-form-subtitle': {
        fontWeight: 'normal',
      },
    },

    '.okta-form-infobox-error': {
      p: {
        fontSize: 12,
      },
    },
    // V6 styles, may no longer need under here

    '.auth-header': {
      minHeight: 30,
    },
    '.idp-discovery, .primary-auth': {
      // sign in form
      marginTop: '40px',
    },

    // reset password
    '.password-reset-email-sent': {
      marginTop: '-30px',
      '.o-form-explain': {
        fontWeight: 'normal !important',

        fontSize: '14px !important',
        textTransform: 'unset',
      },

      '.accessibility-text': {
        display: 'none',
      },

      '.o-form-fieldset-container': {
        marginTop: '20px',
        textAlign: 'center',
      },

      'a.button.link-button': commonButtonStyles,
    },

    '.select-authenticator-authenticate': {
      '.identifier-container, .authenticator-list-title': {
        display: 'none',
      },
    },

    //  MFA SET UP FORM
    '.enroll-choices, .select-authenticator-authenticate,': {
      '.okta-form-subtitle': {
        textTransform: 'none',
      },

      '.authenticator-label': {
        margin: 0,
        marginTop: 3,
      },

      '.authenticator-description--text ': {
        margin: 0,
      },

      fontWeight: 'normal !important',
      'a.button.link-button': commonButtonStyles,

      '.enroll-factor-description': {
        '> p': {
          marginY: '4px !important',
        },
        marginBottom: '30px',
      },
      '.enroll-factor-label': {
        marginBottom: 0,
      },
    },

    // MFA SUB FORM (set up factor)
    '.enroll-totp': {
      '.o-form-fieldset': {
        border: 'none',
      },

      '.instructions > a': commonButtonStyles,

      a: commonButtonStyles,

      '.custom-radio': {
        input: {
          marginRight: '5px',
        },
      },
    },

    // verify code from email form
    '.mfa-verify': {
      '.mfa-email-sent-content': {
        fontWeight: 'normal',
      },
      // a: commonButtonStyles,

      '.resend-email-btn': {
        marginLeft: '6px',
      },

      '.auth-footer': {
        paddingTop: '10px',
      },

      '.o-form-input-name-rememberDevice': {
        '.custom-checkbox': {
          label: {
            fontWeight: 'normal !important',
            fontSize: '14px !important',
          },
        },
      },
    },

    // get a push notification confirmation/screen, verify a code
    '.okta-verify-push-challenge, .okta-verify-resend-push, .okta-verify-totp-challenge': {
      '.identifier-container': {
        display: 'none',
      },

      a: {
        display: 'none',
      },
    },

    // initial activate email code form
    '.enroll-activate-email': {
      '.enroll-activate-email-content': {
        fontWeight: 'normal',
      },
      a: commonButtonStyles,

      '.resend-email-btn': {
        marginLeft: '6px',
      },
    },

    // Set up email factor
    '.enroll-email': {
      '> form': {
        div: {
          fontWeight: 'normal',
        },
      },
    },

    // enroll sms
    '.enroll-sms': {
      ' .o-form-label-top:first-of-type': {
        // hide the country drop down (it blows up the page, and is united states already)
        display: 'none',
      },

      a: commonButtonStyles,

      '.sms-request-button': {
        marginLeft: '0px !important',
      },
    },

    // Set up Okta Verify factor
    '.barcode-push': {
      '.scan-instructions-details > p': {
        fontWeight: 'normal',
      },
      '.scan-instructions-qrcode-wrapper': {
        textAlign: 'center',
        a: commonButtonStyles,

        '.manual-setup': {
          // hide the 'cant scan' for now as its really broken
          display: 'none',
        },
        // '.refresh-qrcode': {
        //   marginLeft: '8px',
        // },
      },
    },

    // "cant scan / enroll okta verify manually form"
    '.enroll-manual-push': {
      '.o-form-explain': {
        textTransform: 'none',
        fontWeight: 'normal',

        a: commonButtonStyles,
      },
    },
  },
};
